import React, {
  useCallback,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';

const LOGIN_PAGE_STATE = Object.freeze({
  START: 'START',
  FORGOTPASS: 'FORGOTPASS',
  RESETPASS: 'RESETPASS',
  RESETPASSCODE: 'RESETPASSCODE',
});

export const FORM_IDS = Object.freeze({
  USERNAME: 'username',
  PASSWORD: 'password',
  SITEID: 'siteId',
  EMAIL: 'email',
  RESETCODE: 'resetCode',
  NEWPASSWORD: 'newPassword',
  CONFIRMPASSWORD: 'confirmPassword',
});

export const formDefaultValues = {
  username: '',
  password: '',
  siteId: '',
  email: '',
  resetCode: '',
  newPassword: '',
  confirmPassword: '',
};

const LoginContext = createContext();

export function useLoginContext() {
  return useContext(LoginContext);
}

const initialState = {
  currentPage: LOGIN_PAGE_STATE.START,
  globalMessage: undefined,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };
    case 'SET_GLOBAL_MESSAGE':
      return {
        ...state,
        globalMessage: action.payload,
      };
    default:
      return state;
  }
};

export default function LoginProvider({ children, defaultValues: bag }) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    formBag: bag ?? initialState.formBag,
  });

  const { pages, totalPages } = useMemo(() => {
    return {
      pages: LOGIN_PAGE_STATE,
      totalPages: Object.keys(LOGIN_PAGE_STATE).length,
    };
  }, [Object.keys(LOGIN_PAGE_STATE).length]);

  const setCurrentPage = useCallback((page) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
  }, []);

  /**
   * variable passed here will either be undefined or contain an object with
   * message: string
   * severity: string
   */
  const setGlobalMessage = useCallback((object = undefined) => {
    dispatch({ type: 'SET_GLOBAL_MESSAGE', payload: object });
  }, []);

  // reset is not currently used. It is being left here for possible future use, but will need updates prior to use.
  const reset = useCallback(() => {
    setCurrentPage(LOGIN_PAGE_STATE.START);
  }, []);

  // Note on useMemo:
  // The state reference remains the same across renders
  // So using "state" is suitable for useHook dependency arrays
  // like useMemo here.
  const value = useMemo(
    () => ({
      state,
      actions: {
        reset,
        setCurrentPage,
        setGlobalMessage,
      },
      meta: {
        pages,
        totalPages,
      },
    }),
    [state]
  );

  return (
    <LoginContext.Provider value={value}>{children}</LoginContext.Provider>
  );
}

LoginProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultValues: PropTypes.object,
};
